import React, { useState } from 'react';
import { Shield, Sparkles, Zap, Car, Wrench, Settings, Check, ArrowRight, Star, Clock, DollarSign, Gauge } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from '../mock';
import TuningEnquiry from '../components/TuningEnquiry';

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [showTuningEnquiry, setShowTuningEnquiry] = useState(false);
  const [selectedTuningService, setSelectedTuningService] = useState(null);

  const getServiceIcon = (iconName) => {
    const icons = {
      Shield: Shield,
      Sparkles: Sparkles,
      Zap: Zap,
      Car: Car,
      Wrench: Wrench,
      Settings: Settings
    };
    return icons[iconName] || Shield;
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1647923320920-9c9da74f7419?crop=entropy&cs=srgb&fm=jpg&ixid=M3w2ODU4NDd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBhdXRvbW90aXZlfGVufDB8fHx8MTc1ODIxOTE5M3ww&ixlib=rb-4.1.0&q=85" 
            alt="Luxury automotive services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/80"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Premium
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Services</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Comprehensive automotive solutions designed for luxury and premium vehicles
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.services.map((service) => {
              const IconComponent = getServiceIcon(service.icon);
              return (
                <div 
                  key={service.id} 
                  className="relative bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700 hover:border-amber-500/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl cursor-pointer overflow-hidden"
                  onClick={() => setSelectedService(service)}
                >
                  {/* Card Background Image */}
                  <div className="absolute inset-0 z-0">
                    <img 
                      src={service.id === 1 ? "https://images.unsplash.com/photo-1622691972122-03104c7e0a03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w2ODU4NDd8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBhdXRvbW90aXZlfGVufDB8fHx8MTc1ODIxOTE5M3ww&ixlib=rb-4.1.0&q=85" :
                           service.id === 2 ? "https://images.unsplash.com/photo-1657710190248-9506090f81e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w2ODU4NDd8MHwxfHNlYXJjaHwyfHxjYXIlMjBkZXRhaWxpbmd8ZW58MHx8fHwxNzU4MjE5MTQ2fDA&ixlib=rb-4.1.0&q=85" :
                           "https://images.pexels.com/photos/7233113/pexels-photo-7233113.jpeg"} 
                      alt={service.title}
                      className="w-full h-full object-cover opacity-10"
                    />
                  </div>
                  
                  <div className="relative z-10">
                    <div className="space-y-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                        <IconComponent className="text-slate-900" size={32} />
                      </div>
                      
                      <div className="space-y-3">
                        <h3 className="text-2xl font-bold text-white">{service.title}</h3>
                        <p className="text-slate-300 text-sm leading-relaxed">{service.description}</p>
                      </div>

                      <div className="space-y-2">
                        {service.features.map((feature, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <Check className="text-amber-400 flex-shrink-0" size={16} />
                            <span className="text-slate-400 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <button className="w-full bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 text-amber-400 py-3 rounded-lg font-semibold hover:from-amber-500/20 hover:to-orange-500/20 transition-all duration-200 flex items-center justify-center space-x-2">
                        <span>Learn More</span>
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tuning Services Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Performance
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Tuning Services</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Unlock your vehicle's true potential with our expert tuning and performance optimization services
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {mockData.tuning.services.map((service) => {
              const ServiceIcon = service.id === 1 ? Gauge : service.id === 2 ? Settings : service.id === 3 ? Zap : Wrench;
              
              return (
                <div 
                  key={service.id} 
                  className="bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group border border-gray-700"
                >
                  <div className="relative">
                    <img 
                      src={service.image} 
                      alt={service.name}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {service.popular && (
                      <div className="absolute top-4 right-4 bg-amber-500 text-gray-900 px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                        <Star className="w-4 h-4 mr-1" />
                        Popular
                      </div>
                    )}
                  </div>
                  
                  <div className="p-8">
                    <div className="flex items-center mb-4">
                      <ServiceIcon className="w-8 h-8 text-amber-400 mr-3" />
                      <h3 className="text-2xl font-bold text-white">
                        {service.name}
                      </h3>
                    </div>
                    
                    <p className="text-gray-300 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-white mb-3">Service Features:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="flex items-center justify-between mb-6 text-sm text-gray-400">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        {service.duration}
                      </div>
                      <div className="flex items-center font-semibold text-amber-400">
                        <DollarSign className="w-4 h-4 mr-1" />
                        {service.price}
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => {
                        setSelectedTuningService(service);
                        setShowTuningEnquiry(true);
                      }}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-gray-900 py-3 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                    >
                      Enquire Now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tuning Enquiry Form */}
      {showTuningEnquiry && (
        <TuningEnquiry 
          selectedService={selectedTuningService}
          onClose={() => {
            setShowTuningEnquiry(false);
            setSelectedTuningService(null);
          }}
        />
      )}

      {/* Service Details Modal */}
      {selectedService && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-700">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                    {React.createElement(getServiceIcon(selectedService.icon), { className: "text-slate-900", size: 24 })}
                  </div>
                  <h2 className="text-3xl font-bold text-white">{selectedService.title}</h2>
                </div>
                <button 
                  onClick={() => setSelectedService(null)}
                  className="text-slate-400 hover:text-white transition-colors p-2"
                >
                  ×
                </button>
              </div>

              <p className="text-slate-300 text-lg leading-relaxed">{selectedService.description}</p>

              <div className="space-y-4">
                <h3 className="text-xl font-bold text-white">Key Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedService.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3 bg-slate-800/50 p-3 rounded-lg">
                      <Check className="text-amber-400 flex-shrink-0" size={16} />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-slate-700">
                <Link
                  to="/contact"
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 py-4 rounded-lg font-bold text-center block hover:from-amber-400 hover:to-orange-400 transition-all duration-200"
                  onClick={() => setSelectedService(null)}
                >
                  Get Quote for This Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Why Choose Us Section */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-white">
              Why Choose
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Unisol Global?</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Decades of expertise and commitment to excellence in every service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full mx-auto flex items-center justify-center">
                <Shield className="text-slate-900" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white">Premium Quality</h3>
              <p className="text-slate-400 text-sm">Only the finest materials and techniques for your luxury vehicle</p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full mx-auto flex items-center justify-center">
                <Car className="text-slate-900" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white">Expert Technicians</h3>
              <p className="text-slate-400 text-sm">Trained professionals with 25+ years of combined experience</p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full mx-auto flex items-center justify-center">
                <Sparkles className="text-slate-900" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white">Latest Technology</h3>
              <p className="text-slate-400 text-sm">Cutting-edge equipment and advanced protection systems</p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full mx-auto flex items-center justify-center">
                <Settings className="text-slate-900" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white">Warranty Backed</h3>
              <p className="text-slate-400 text-sm">Comprehensive warranties on all our premium services</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-sm border-y border-amber-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl font-bold text-white">
            Ready to Protect
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500"> Your Investment?</span>
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed">
            Contact us today for a personalized consultation and discover how our premium services 
            can enhance and protect your luxury vehicle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:from-amber-400 hover:to-orange-400 transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              Get Your Quote
            </Link>
            <Link
              to="/gallery"
              className="border-2 border-amber-400 text-amber-400 px-8 py-4 rounded-lg font-bold text-lg hover:bg-amber-400 hover:text-slate-900 transition-all duration-200"
            >
              View Our Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;